import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Globals } from '../../globals';
import {
  AddSharedAccountRequest,
  GetSharedAccountResult,
  SharedAccount,
  UpdateSharedAccountGroupsRequest,
} from './shared-accounts.interfaces';

@Injectable({
  providedIn: 'root',
})
export class SharedAccountsService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);


  /**
   * Get all shared accounts
   */
  getSharedAccounts(): Observable<SharedAccount[]> {
    const url = `${this.globals.apiRoot}/jwtsharedaccounts`;
    return this.http.get<SharedAccount[]>(url);
  }

  /**
   * Get all shared guests accounts
   */
  getSharedGuestAccounts(): Observable<SharedAccount[]> {
    const url = `${this.globals.apiRoot}/jwtsharedguestaccounts`;
    return this.http.get<SharedAccount[]>(url);
  }

  /**
   * Get the first, pending shared guests account
   */
  getFirstPendingSharedGuestAccounts(): Observable<SharedAccount> {
    const url = `${this.globals.apiRoot}/jwtsharedguestaccounts/pending/first`;
    return this.http.get<SharedAccount>(url);
  }

  /**
   * Get a shared account
   * @param id the id of the shared account
   */
  getSharedAccount(id: number): Observable<SharedAccount> {
    const url = `${this.globals.apiRoot}/jwtsharedaccounts/${id}`;
    return this.http.get<SharedAccount>(url);
  }

  /**
   * Returns false if the current user was already invited and has a pending/accepted invitation
   */
  canInvite(email: string) {
    const url = `${this.globals.apiRoot}/jwtsharedaccounts/${email}/caninvite`;
    return this.http.get<boolean>(url);
  }

  addSharedAccount(request: AddSharedAccountRequest): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtSharedAccountsV2`;
    return this.http.post<void>(url, request);
  }

  /**
   * Delete a shared account
   * @param id the shared account id
   */
  deleteSharedAccount(id: number): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtsharedaccounts/${id}`;
    return this.http.delete<boolean>(url);
  }

  /**
   * Delete a shared guest account
   * @param id the shared guest account id
   */
  deleteSharedGuestAccount(id: number): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtsharedguestaccounts/${id}`;
    return this.http.delete<boolean>(url);
  }

  /**
   * Get a count of pending shared guest accounts for the currently logged in user
   */
  getPendingSharedGuestAccountsCount(): Observable<number> {
    const url = `${this.globals.apiRoot}/jwtsharedguestaccounts/pending/count`;
    return this.http.get<number>(url);
  }

  /**
   * Accept a shared guest account
   * @param id the shared guest account id
   */
  acceptSharedGuestAccount(sharedAccount: SharedAccount): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtsharedguestaccounts/${sharedAccount.id}/accept`;
    return this.http.put<boolean>(url, sharedAccount);
  }

  /**
   * Deny a shared guest account
   * @param id the shared guest account id
   */
  declineSharedGuestAccount(sharedAccount: SharedAccount): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtsharedguestaccounts/${sharedAccount.id}/decline`;
    return this.http.put<boolean>(url, sharedAccount);
  }

  getSharedAccountGroups(
    sharedAccountid: number
  ): Observable<GetSharedAccountResult> {
    const url = `${this.globals.apiRoot}/jwtSharedAccounts/${sharedAccountid}/groups`;
    return this.http.get<GetSharedAccountResult>(url);
  }

  updateSharedAccountGroups(
    sharedAccountid: number,
    request: UpdateSharedAccountGroupsRequest
  ): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtSharedAccounts/${sharedAccountid}/groups`;
    return this.http.put<void>(url, request);
  }
}
